import React, {useCallback, useDeferredValue, useEffect, useRef, useMemo} from 'react';
import {hashKey, useQuery, useSuspenseQuery, useQueryClient, usePrefetchQuery, keepPreviousData} from '@tanstack/react-query';
import {useLiveUpdatesForSubtree} from '@tlg-contenthub/live-preview/react';

import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';

import useLocalesMgr from '../useLocalesMgr';
import useRefetchOnMount from '../useRefetchOnMount';
import useShellGate from '../useShellGate';
import type CMSProvider from 'coreClasses/content/providers/CMSProvider';
import {resolveV2Includes} from 'coreUtils/resolveV2Includes';
import {wrapItemsAsCollection} from 'coreUtils/contenthubResponseNormalizer';
import localizeContent from 'coreUtils/localizeContent';
import {applyCmsSiteFilter, buildCmsContentQueryKey} from './buildCmsQueryKey';
import useCmsProvider from 'coreHooks/useCmsContent/useCmsProvider';
import {getSiteByCountryCode} from 'coreUtils/siteUtils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type CmsVariableValue =
	| string
	| string[]
	| number
	| boolean
	| Record<string, string>
	| CmsVariableObject;

interface CmsVariableObject {
	[key: string]: CmsVariableValue;
}

export type CmsVariables = Record<string, CmsVariableValue>;

type CmsConfig = {
	variables?: CmsVariables;
	fallbackVariables?: CmsVariables[];
};

interface CmsV2RawResponse {
	items: unknown[];
	includes?: {Entry?: unknown[]; Asset?: unknown[]};
	[key: string]: unknown;
}

export interface UseCmsContentOptions {
	contentType: string;
	config?: CmsConfig;
	collectionKey: string;
	enabled?: boolean;
	deps?: React.DependencyList;
	/** When `true`, automatically adds a `sites` variable with `[currentSiteId, 'all']` (or `['all']` when locale is invalid). */
	filterSite?: boolean;
	/** Time in ms that fetched data is considered fresh before a background refetch. */
	staleTime?: number;
	/** Time in ms that unused/inactive cache data remains in memory. */
	gcTime?: number;
	/**
	 * Controls whether React Query re-fetches when the component mounts on CSR.
	 * - `true` (default): re-fetch if data is stale (standard behavior).
	 * - `false`: never re-fetch on mount.
	 * - `'always'`: force a background re-fetch on mount regardless of `staleTime`.
	 */
	refetchOnMount?: boolean | 'always';
}

export interface UseCmsContentResult<T = any> {
	data: T | null;
	isLoading: boolean;
	error: Error | null;
	/** `true` whenever a fetch is in progress (initial load or background refetch). */
	isFetching: boolean;
	/** `true` when showing previous cached data while a new query key is loading. */
	isPlaceholderData: boolean;
}

export interface UseSuspenseCmsContentResult<T = any> {
	data: T | null;
	isLoading: false;
	error: null;
	isFetching: false;
	isPlaceholderData: false;
}

const DISABLED_SUSPENSE_RESULT: UseSuspenseCmsContentResult = {
	data: null,
	isLoading: false,
	error: null,
	isFetching: false,
	isPlaceholderData: false,
};

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

/**
 * Builds the React Query key and `queryFn` from the given options.
 * Pure function — no hooks, safe to call anywhere.
 */
function buildCmsQueryOptions<T>(
	cmsProvider: CMSProvider,
	contentType: string,
	config: CmsConfig | undefined,
	currentLanguage: string,
	deps: React.DependencyList,
) {
	const queryKey = buildCmsContentQueryKey({
		contentType,
		providerId: cmsProvider.getId(),
		config,
		currentLanguage,
		deps,
	});

	const queryFn = async (): Promise<T | null> => {
		let result = await cmsProvider.getContent(contentType, config, false, false);

		if (!result) {
			result = await cmsProvider.getBackupContent(contentType);
		}

		return result as T | null;
	};

	return {queryKey, queryFn};
}

/**
 * Subscribes to Contentful live-preview updates and invalidates the
 * given query whenever a new revision is published.
 */
function useCmsLivePreview<T>(
	lastValidDataRef: {current: T | null},
	collectionKey: string,
	queryKey: readonly unknown[],
) {
	const response = lastValidDataRef.current as Record<string, {items?: Array<{sys?: {id?: string}}>}> | null;
	const entryId = response?.[collectionKey]?.items?.[0]?.sys?.id;
	const {revision} = useLiveUpdatesForSubtree([entryId]);
	const queryClient = useQueryClient();

	useEffect(() => {
		if (revision > 0) {
			queryClient.invalidateQueries({queryKey});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [revision]);

	return revision;
}

/**
 * Resolves the CMS provider, current language and site filter, then builds the query options.
 */
function useCmsQueryOptions<T>({
	contentType,
	config,
	deps = [],
	filterSite = false
}: Pick<UseCmsContentOptions, 'contentType' | 'config' | 'deps' | 'filterSite'>) {
	const cmsProvider = useCmsProvider();
	const localesMgr = useLocalesMgr();
	const currentLanguage = localesMgr.getCurrentLanguageCode();

	let effectiveConfig = config;

	if (filterSite) {
		const country = localesMgr?.getCurrentCountry() || localesMgr?.getDefaultCountry();
		let siteId = country?.getSiteId();

		if (!siteId) {
			siteId = getSiteByCountryCode(country?.getCountryCode())?.id;
		}

		effectiveConfig = applyCmsSiteFilter(config, siteId);
	}

	return buildCmsQueryOptions<T>(
		cmsProvider,
		contentType,
		effectiveConfig,
		currentLanguage,
		deps,
	);
}

/**
 * Shared setup of the public hooks: query options, live-preview invalidation, `select`.
 */
function useCmsQuerySetup<T>({
	collectionKey,
	...options
}: Pick<
	UseCmsContentOptions,
	'contentType' | 'config' | 'collectionKey' | 'deps' | 'filterSite'
>) {
	const {contentType} = options;
	const lastValidDataRef = useRef<T | null>(null);
	const {queryKey, queryFn} = useCmsQueryOptions<T>(options);

	const revision = useCmsLivePreview(lastValidDataRef, collectionKey, queryKey);
	const selectCmsData = useCallback(
		(data: T | null): T | null => {
			// `null` when neither the primary nor the fallback variables return content.
			if (!data || !data.includes) {
				return data;
			}

			const raw = {...data} as unknown as CmsV2RawResponse;

			if (raw.includes) {
				resolveV2Includes(raw);
			}

			return wrapItemsAsCollection(
				collectionKey || contentType.charAt(0).toLowerCase() + contentType.slice(1),
				raw.items || [],
			) as T;
		},
		[contentType],
	);

	return {queryKey, queryFn, lastValidDataRef, revision, selectCmsData};
}

// ---------------------------------------------------------------------------
// Public hooks
// ---------------------------------------------------------------------------

/**
 * Fetches CMS content via `useQuery` and automatically refetches when
 * Contentful live preview publishes an update.
 *
 * Results are cached per `contentType` + `currentLanguage` + `deps`.
 * `keepPreviousData` ensures smooth visual transitions when the query key
 * changes (e.g. language switch).
 *
 * @param options.contentType  Query name passed to `cmsProvider.getContent`
 * @param options.config       Variables / config forwarded to `getContent`
 * @param options.collectionKey  Top-level collection key in the response (e.g. 'headerCollection').
 *                               Used to extract `items[0].sys.id` for live-preview subscriptions.
 * @param options.enabled      Consumer-controlled gate; when false the fetch is skipped
 *                             unless a live-preview revision fires (default true)
 * @param options.deps         Extra dependency list entries that trigger a refetch
 * @param options.staleTime    How long (ms) fetched data is considered fresh (default 0)
 * @param options.gcTime       How long (ms) unused cache data remains in memory (default 5 min)
 * @param options.refetchOnMount  Controls CSR mount refetch (`true` | `false` | `'always'`)
 */
function useCmsContent<T = any>({
	enabled = true,
	refetchOnMount,
	staleTime,
	gcTime,
	...setupOptions
}: UseCmsContentOptions): UseCmsContentResult<T> {
	const {queryKey, queryFn, lastValidDataRef, revision, selectCmsData} = useCmsQuerySetup<T>(setupOptions);
	const refetchOnFirstMount = useRefetchOnMount(refetchOnMount);

	// revision > 0 means a live-preview update happened — override enabled=false
	// so the invalidated query can actually refetch.
	const computedEnabled = enabled || revision > 0;

	const query = useQuery<T | null, Error, T | null>({
		queryKey,
		queryFn,
		select: selectCmsData,
		enabled: computedEnabled,
		placeholderData: keepPreviousData,
		refetchOnMount: refetchOnFirstMount,
		...(staleTime !== undefined && {staleTime}),
		...(gcTime !== undefined && {gcTime}),
	});

	if (query.data !== null && query.data !== undefined) {
		lastValidDataRef.current = query.data;
	}

	const data = (query.data ?? lastValidDataRef.current) as T;

	return useMemo(
		() => ({
			data,
			isLoading: query.isFetching,
			error: query.error,
			isFetching: query.isFetching,
			isPlaceholderData: query.isPlaceholderData,
		}),
		[data, query.isFetching, query.error, query.isPlaceholderData],
	);
}

/**
 * Suspense variant of {@link useCmsContent}. Uses `useSuspenseQuery` — the
 * component suspends until data resolves and must be wrapped in `<Suspense>`.
 *
 * When `enabled` is `false` no CMS call is made and `data` is `null`.
 * Toggling `enabled` back to `true` triggers a fresh suspense fetch.
 */
export function useSuspenseCmsContent<T = any>({
	enabled = true,
	refetchOnMount,
	staleTime,
	gcTime,
	...setupOptions
}: UseCmsContentOptions): UseSuspenseCmsContentResult<T> {
	const {queryKey, queryFn, lastValidDataRef, selectCmsData} = useCmsQuerySetup<T>(setupOptions);
	const refetchOnFirstMount = useRefetchOnMount(refetchOnMount);

	// Defer the serialised query key so that locale (or other dep) changes
	// don't cause synchronous suspension during an urgent Redux render.
	// The old cached entry stays active; the new key loads in a background
	// transition render where Suspense is handled gracefully.
	const keyJson = JSON.stringify(queryKey);
	const deferredKeyJson = useDeferredValue(keyJson);
	const deferredQueryKey: readonly unknown[] = useMemo(
		() => JSON.parse(deferredKeyJson) as unknown[],
		[deferredKeyJson],
	);
	const deferredQueryFn = useMemo(() => queryFn, [deferredKeyJson]); // eslint-disable-line react-hooks/exhaustive-deps

	const effectiveQueryKey = enabled ? deferredQueryKey : [...deferredQueryKey, '__disabled'];
	const effectiveQueryFn = enabled ? deferredQueryFn : async (): Promise<T | null> => null;
	const effectiveSelect = enabled ? selectCmsData : (value: T | null): T | null => value;
	// Server: the shell waits for this content, see `coreUtils/ssrShell`.
	const gate = useShellGate();
	const gateId = gate && `cms:${hashKey(effectiveQueryKey)}`;

	gate?.begin(gateId);

	const {data} = useSuspenseQuery<T | null, Error, T | null>({
		queryKey: effectiveQueryKey,
		queryFn: effectiveQueryFn,
		select: effectiveSelect,
		refetchOnMount: refetchOnFirstMount,
		...(staleTime !== undefined && {staleTime}),
		...(gcTime !== undefined && {gcTime}),
	});

	gate?.end(gateId);

	if (data !== null && data !== undefined) {
		lastValidDataRef.current = data;
	}

	const returnData = enabled ? ((data ?? lastValidDataRef.current) as T) : null;

	return useMemo((): UseSuspenseCmsContentResult<T> => {
		if (!enabled) {
			return DISABLED_SUSPENSE_RESULT;
		}

		return {
			data: returnData,
			isLoading: false,
			error: null,
			isFetching: false,
			isPlaceholderData: false,
		};
	}, [enabled, returnData]);
}

/**
 * Starts the CMS query without suspending (`usePrefetchQuery`). Call it before another suspending
 * hook with the same options as the later `useSuspenseCmsContent`: both requests run in parallel
 * instead of one after the other, and `useSuspenseCmsContent` reads the in-flight query.
 */
export function usePrefetchCmsContent({
	enabled = true,
	contentType,
	config,
	deps,
	filterSite,
}: Omit<UseCmsContentOptions, 'collectionKey'>): void {
	const {queryKey, queryFn} = useCmsQueryOptions({contentType, config, deps, filterSite});

	usePrefetchQuery({
		queryKey: enabled ? queryKey : [...queryKey, '__disabled'],
		queryFn: enabled ? queryFn : async () => null,
	});
}

export const useLocalizeContent = (content: unknown) => {
	const {managerRepository} = useGeneralProvider();
	const cmsProvider = useCmsProvider();
	const localesMgr = useLocalesMgr();
	const currentLocaleCode = localesMgr.getCurrentLocale().getCode();

	return useMemo(() => {
		return localizeContent(cmsProvider, content);
	}, [content, cmsProvider, managerRepository, currentLocaleCode]);
};

export default useCmsContent;
